import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { CircleAlert } from 'lucide-react'
import { DataTable } from '@/components/ui/data-table'
import { withScheme } from '@/utils/url'
import { statMetricColumns } from './statsColumns'
import { useStatLabel } from '../hooks/useStatLabel'
import { StatFilterBar, useStatThresholds } from './StatThresholdFilter'
import type { BreakdownTableProps } from './TargetingTotalsTable'
import {
  ARTICLE_STAT_METRICS,
  useArticleStats,
  type ArticleStatsRow,
} from '../hooks/useArticleStats'

/**
 * The article as two lines: the headline-ish tail of the path, and the host it
 * came from. Articles from the same site otherwise differ only in the part of
 * the URL that falls off the end of the column. The bucket the API sums the
 * long tail into has no URL to show, so it is named rather than linked.
 */
function ArticleCell({ row }: { row: ArticleStatsRow }) {
  const { t } = useTranslation()
  // Article keys usually arrive without a scheme, which would make the link relative.
  const url = withScheme(row.url)
  if (row.isOthers) {
    return <span className="text-sm text-(--ad-text-muted)">{t('stats.allOthers')}</span>
  }
  let path = url
  let host = ''
  try {
    const parsed = new URL(url)
    host = parsed.host
    path = decodeURIComponent(parsed.pathname).replace(/\/$/, '').split('/').pop() || parsed.host
  } catch {
    // Not every article key parses as a URL — show it as it came.
  }
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        title={url}
        className="block truncate font-semibold text-(--ad-link) hover:underline"
      >
        {path}
      </a>
      <span className="truncate text-xs text-(--ad-text-muted)">{host || url}</span>
    </div>
  )
}

/** The articles this line item's ads were served against, busiest first. */
export function ArticleStatsTable({ query, range, maxHeight }: BreakdownTableProps) {
  const { t } = useTranslation()
  const { rows, isLoading, error } = useArticleStats(query, range)
  const { thresholds, setThresholds, filtered } = useStatThresholds(
    rows,
    (row) => row.stats,
    ARTICLE_STAT_METRICS
  )
  const statLabel = useStatLabel()

  const columns = useMemo(
    (): ColumnDef<ArticleStatsRow, unknown>[] => [
      {
        id: 'article',
        header: t('stats.article'),
        size: 280,
        meta: { label: t('stats.article'), minWidth: 200 },
        accessorFn: (row) => row.url,
        cell: ({ row }) => <ArticleCell row={row.original} />,
      },
      ...statMetricColumns<ArticleStatsRow>({
        metrics: ARTICLE_STAT_METRICS,
        statsFor: (row) => row.stats,
        label: statLabel,
      }),
    ],
    [t, statLabel]
  )

  if (error) {
    return (
      <div className="flex items-center gap-2 px-(--card-spacing) py-4 text-destructive">
        <CircleAlert className="size-4" />
        <span className="text-sm">{error.message}</span>
      </div>
    )
  }

  return (
    <>
      <StatFilterBar
        metrics={ARTICLE_STAT_METRICS}
        value={thresholds}
        onChange={setThresholds}
        shown={filtered.length}
        total={rows.length}
      />
      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        getRowId={(row) => row.url}
        emptyMessage={t('stats.noArticleStats')}
        columnsKey="breakdownArticles"
        maxHeight={maxHeight}
        inset
      />
    </>
  )
}
